import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { SearchCombobox } from '@plato/ui-legacy/components/search-combobox/search-combobox';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { api } from '../../../../utils/api.ts';
import { safeJoin } from '../../../../utils/string-utils.ts';
import { useAnalytics } from '../../../analytics/hooks/use-analytics.ts';

type SubordinatesSelectProps = {
    className?: string;
    onValueChange: (value: string | null) => void;
    value: string | null;
    placeholder?: string;
};

type User = {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
};

export function SubordinatesSelect({
    className,
    onValueChange,
    value,
    placeholder,
}: SubordinatesSelectProps) {
    const { t } = useTranslation();
    const { userId } = useAppConfig();
    const sendEvent = useAnalytics();
    const { data: subordinates, isLoading } =
        api.platoUser.query.users.getManyUsers.getSubordinates.useQuery();

    const users = useMemo(() => {
        if (subordinates) {
            return subordinates.map((user: Omit<User, 'fullName'>) => ({
                ...user,
                fullName: safeJoin(
                    [user.firstName, user.lastName, user.id === userId ? `(${t('you')})` : ''],
                    ' ',
                ),
            }));
        }
        return [];
    }, [subordinates, t, userId]);

    const groupedItems = useMemo(() => {
        return [{ id: 'subordinates', items: users }];
    }, [users]);

    if (!isLoading && users.length === 1) {
        return null;
    }

    return (
        <SearchCombobox
            className={className}
            isLoading={isLoading}
            groupedItems={groupedItems}
            notFoundPlaceholder={t('nothing_found')}
            inputPlaceholder={placeholder || t('select_user')}
            keySelector={(user) => user.id}
            valueSelector={(user) => user.id}
            keywordsSelector={(user) => [user.firstName, user.lastName, user.fullName]}
            selectedValue={value ?? userId}
            onValueChange={(newValue) => {
                const isViewingOwnCalendar = newValue === userId;
                if (!isViewingOwnCalendar) {
                    sendEvent('event_team_member_calendar_viewed', {
                        selected_user_id: newValue,
                    });
                }
                onValueChange(isViewingOwnCalendar ? null : newValue);
            }}
            renderItem={(user) => <Text>{user.fullName}</Text>}
            renderSelectedItem={(user) => <Text>{user.fullName}</Text>}
            disabled={isLoading}
        />
    );
}
